import { LoadingButton } from "@mui/lab";
import { Box, Button } from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CertFromCsv from "../components/CertFromCsv";
import ConfirmationDialog from "../components/ConfirmationDialog";
import { IInput } from "../entities/certificates";
import { CERTIFICATION_MANDATORY_COLUMNS, CERTIFICATION_MANDATORY_COLUMNS_HEADER } from "../lib/referentiel";
import { AppDispatch } from "../lib/store";
import { createBatchPreCertificates } from "../lib/store/projects/thunks";

export const NewCertificate: React.FC = () => {
    const navigate = useNavigate();

    const dispatch = useDispatch<AppDispatch>();
    const [loading, setLoading] = useState<boolean>(false);
    const [openConfirmationDialog, setOpenConfirmationDialog] = useState<boolean>(false);
    const [batchInputs, setBatchInputs] = useState<IInput[]>();
    const [csvKey, setCsvKey] = useState<number>(0);


    const uploadBatchCert = (inputs: IInput[]) => {
        if (inputs && inputs.length == 0) {
            toast.error("données corrompues");
            return;
        }
        setLoading(true);
        dispatch(createBatchPreCertificates(inputs)).unwrap()
            .then(() => toast.success('Pré-certificats créé avec succės'))
            .then(() => navigate(-1))
            .catch(() => toast.error('Une erreur est survenue lors de la pre-certification, veuillez contacter le support si cela persiste...'))
            .finally(() => {
                setLoading(false);
                setOpenConfirmationDialog(false);
            });
    }

    const downloadCsvTemplate = (filename: string, csvContent: string) => {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    const downloadLicenceTemplate = () => {
        const csvContent = `institution,nom,prenoms,ine,matricule,date_de_naissance,lieu_de_naissance,session,programme,specialite,diplome,mention,finalite,moyenne,label_signataire_1,signataire_1,label_signataire_2,signataire_2,date_du_conseil
UGLCSC,CURTIS,MAURICE ABOUBACAR,CUMA1911147927,12027391633,11/19/1998,CONAKRY,2018-2019,SCIENCES POLITIQUES,ADMINISTRATION PUBLIQUE,LICENCE,PASSABLE,PROFESSIONNELLE,14.10,EXEMPLE: Le Chef Service des Études Avancées,EXEMPLE: PR Mohamed Moustapha DIOP,EXEMPLE: Le Recteur,EXEMPLE: Pr Daniel LAMAH,EXEMPLE: 12 Octobre 2025`;
        downloadCsvTemplate('template_import_diplome_licence.csv', csvContent);
    }

    const downloadIngenieurTemplate = () => {
        const csvContent = `institution,nom,prenoms,ine,matricule,date_de_naissance,lieu_de_naissance,session,programme,specialite,diplome,mention,finalite,moyenne,label_signataire_1,signataire_1,label_signataire_2,signataire_2,date_du_conseil
UGLCSC,DIALLO,MOHAMED LAMINE,CUMA2011147001,12027391001,03/12/1997,CONAKRY,2021-2022,GENIE CIVIL,BATIMENT ET TRAVAUX PUBLICS,INGENIEUR,BIEN,PROFESSIONNELLE,14.90,EXEMPLE: Le Chef Service des Études Avancées,EXEMPLE: PR Mohamed Moustapha DIOP,EXEMPLE: Le Recteur,EXEMPLE: Pr Daniel LAMAH,EXEMPLE: 10 Juin 2024`;
        downloadCsvTemplate('template_import_diplome_ingenieur.csv', csvContent);
    }

    const downloadMedecineVeterinaireTemplate = () => {
        const csvContent = `institution,nom,prenoms,ine,matricule,date_de_naissance,lieu_de_naissance,session,programme,specialite,diplome,mention,finalite,intitule_these,moyenne,label_signataire_1,signataire_1,label_signataire_2,signataire_2,date_du_conseil
UGLCSC,SOW,FATOUMATA,CUMA2011147110,12027391110,07/22/1996,KINDIA,2020-2021,MEDECINE VETERINAIRE,SANTE ANIMALE,DOCTEUR D'ETAT EN MEDECINE VETERINAIRE,TRES BIEN,FONDAMENTALE,"EXEMPLE: Etude clinique en sante animale",15.80,EXEMPLE: Le Directeur de l'Ecole Doctorale,EXEMPLE: PR Mohamed Moustapha DIOP,EXEMPLE: Le Recteur,EXEMPLE: Pr Daniel LAMAH,EXEMPLE: 18 Septembre 2024`;
        downloadCsvTemplate('template_import_diplome_medecine_veterinaire.csv', csvContent);
    }

    const downloadMasterTemplate = () => {
        const csvContent = `institution,nom,prenoms,ine,matricule,date_de_naissance,lieu_de_naissance,session,programme,specialite,diplome,mention,finalite,moyenne,label_signataire_1,signataire_1,label_signataire_2,signataire_2,date_du_conseil
UGLCSC,DIALLO,AMADOU OUSMANE,CUMA1911147000,12027390000,11/19/1995,CONAKRY,2022-2023,INFORMATIQUE,INTELLIGENCE ARTIFICIELLE,MASTER,TRES BIEN,PROFESSIONNELLE,15.20,EXEMPLE: Le Chef Service des Études Avancées,EXEMPLE: PR Mohamed Moustapha DIOP,EXEMPLE: Le Recteur,EXEMPLE: Pr Daniel LAMAH,EXEMPLE: 15 Juillet 2024`;
        downloadCsvTemplate('template_import_diplome_master.csv', csvContent);
    }

    const downloadDoctoratTemplate = () => {
        const csvContent = `institution,nom,prenoms,ine,matricule,date_de_naissance,lieu_de_naissance,session,programme,specialite,diplome,mention,finalite,intitule_these,moyenne,label_signataire_1,signataire_1,label_signataire_2,signataire_2,date_du_conseil
UGLCSC,CONTE,IBRAHIMA SORY,CUMA1911147999,12027399999,11/19/1990,CONAKRY,2021-2022,MATHEMATIQUES,ANALYSE,DOCTEUR,TRES BIEN,FONDAMENTALE,\"EXEMPLE: Étude des équations différentielles non linéaires\",16.30,EXEMPLE: Le Directeur de l'École Doctorale,EXEMPLE: PR Mohamed Moustapha DIOP,EXEMPLE: Le Recteur,EXEMPLE: Pr Daniel LAMAH,EXEMPLE: 20 Décembre 2023`;
        downloadCsvTemplate('template_import_diplome_docteur.csv', csvContent);
    }

    return (
        <Box
            display="flex"
            flexDirection="column"
            height="100%"
            className="color-chart"
            gap={2}
        >
            <Box display="flex" gap={2} className="color-chart">
                <Button 
                    onClick={downloadLicenceTemplate} 
                    size="small" 
                    variant="outlined" 
                    color="info"
                    startIcon={<DownloadIcon />}
                >
                    Exemple LICENCE
                </Button>
                <Button 
                    onClick={downloadMasterTemplate} 
                    size="small" 
                    variant="outlined" 
                    color="info"
                    startIcon={<DownloadIcon />}
                >
                    Exemple MASTER
                </Button>
                <Button 
                    onClick={downloadIngenieurTemplate} 
                    size="small" 
                    variant="outlined" 
                    color="info"
                    startIcon={<DownloadIcon />}
                >
                    Exemple INGENIEUR
                </Button>
                <Button 
                    onClick={downloadMedecineVeterinaireTemplate} 
                    size="small" 
                    variant="outlined" 
                    color="info"
                    startIcon={<DownloadIcon />}
                >
                    Exemple MEDECINE VETERINAIRE
                </Button>
                <Button 
                    onClick={downloadDoctoratTemplate} 
                    size="small" 
                    variant="outlined" 
                    color="info"
                    startIcon={<DownloadIcon />}
                >
                    Exemple DOCTEUR
                </Button>
                {batchInputs && batchInputs.length > 0 && (
                    <Button
                        onClick={() => {
                            setBatchInputs(undefined);
                            setCsvKey((prev) => prev + 1);
                        }}
                        size="small"
                        variant="outlined"
                        color="warning"
                    >
                        Remplacer le fichier
                    </Button>
                )}
                <LoadingButton
                    onClick={() => setOpenConfirmationDialog(true)}
                    size="small"
                    loading={loading}
                    variant="contained"
                    color="success"
                    type="submit"
                    disabled={!batchInputs || batchInputs.length === 0}
                >
                    Importer
                </LoadingButton>
            </Box>
 
            <Box height="100%">
                <CertFromCsv
                    key={csvKey}
                    mandatory={{columns: CERTIFICATION_MANDATORY_COLUMNS, header: CERTIFICATION_MANDATORY_COLUMNS_HEADER}}
                    setInputs={setBatchInputs}
                />
            </Box>

            <ConfirmationDialog open={openConfirmationDialog} close={() => setOpenConfirmationDialog(false)} actionLabel="Import" loading={loading}  action={() => uploadBatchCert(batchInputs!)}/>
        </Box>
    )
}